export type OklchColor = {
  l: number
  c: number
  h: number
}
export function hslToRgb(h: number, s: number, l: number) {
  s /= 100
  l /= 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  return [255 * f(0), 255 * f(8), 255 * f(4)]
}

export function rgbToHex(r: number, g: number, b: number) {
  return `#${((r << 16) + (g << 8) + b).toString(16).padStart(6, "0")}`
}

export function hslToHex(h: number, s: number, l: number) {
  const [r, g, b] = hslToRgb(h, s, l)
  return rgbToHex(r ?? 0, g ?? 0, b ?? 0)
}

export function oklchToHex(color: OklchColor) {
  const { l, c, h } = color
  // Convert OKLCH to OKLAB
  const a = c * Math.cos((h * Math.PI) / 180)
  const b = c * Math.sin((h * Math.PI) / 180)

  // Convert OKLAB to XYZ
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b

  const l_cubed = l_ * l_ * l_
  const m_cubed = m_ * m_ * m_
  const s_cubed = s_ * s_ * s_

  const x = 1.2270138511 * l_cubed - 0.5577999807 * m_cubed + 0.2812561490 * s_cubed
  const y = -0.0405801784 * l_cubed + 1.1122568696 * m_cubed - 0.0716766787 * s_cubed
  const z = -0.0763812845 * l_cubed - 0.4214819784 * m_cubed + 1.5861632204 * s_cubed

  // Convert XYZ to sRGB
  const r = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z
  const g = -0.9692660 * x + 1.8760108 * y + 0.0415560 * z
  const b_val = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z

  // Apply gamma correction and clamp values
  const gammaCorrect = (v: number) => {
    const abs = Math.abs(v)
    if (abs > 0.0031308) {
      return (Math.sign(v) * 1.055 * abs ** (1 / 2.4) - 0.055) * 255
    }
    return v * 12.92 * 255
  }

  const r_ = Math.round(Math.max(0, Math.min(255, gammaCorrect(r))))
  const g_ = Math.round(Math.max(0, Math.min(255, gammaCorrect(g))))
  const b_ = Math.round(Math.max(0, Math.min(255, gammaCorrect(b_val))))

  return rgbToHex(r_, g_, b_)
}

export function parseOklchCss(cssValue: string): OklchColor {
  // Remove 'oklch(' and ')' and split by spaces
  const values = cssValue.replace("oklch(", "").replace(")", "").split(" ").map(Number)
  return {
    l: values[0] ?? 0,
    c: values[1] ?? 0,
    h: values[2] ?? 0,
  }
}

// Helper: Convert RGB (0-255) to linear RGB (0-1)
function srgbToLinear(val: number) {
  val /= 255
  return val <= 0.04045 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4
}

// Helper: Convert linear RGB to XYZ
function rgbToXyz(r: number, g: number, b: number) {
  // Convert sRGB to linear RGB
  const rl = srgbToLinear(r)
  const gl = srgbToLinear(g)
  const bl = srgbToLinear(b)
  // sRGB D65
  return {
    x: rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375,
    y: rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750,
    z: rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041,
  }
}

// Helper: Convert XYZ to OKLAB
function xyzToOklab(x: number, y: number, z: number) {
  // OKLAB conversion constants
  const l_ = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z
  const m_ = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z
  const s_ = 0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z

  const l = Math.cbrt(l_)
  const m = Math.cbrt(m_)
  const s = Math.cbrt(s_)

  return {
    l: 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  }
}

// Helper: Convert OKLAB to OKLCH
function oklabToOklch(lab: { l: number, a: number, b: number }): OklchColor {
  const { l, a, b } = lab
  const c = Math.sqrt(a * a + b * b)
  const h = (Math.atan2(b, a) * 180) / Math.PI
  return { l, c, h: h < 0 ? h + 360 : h }
}

// Main: Convert HSL to OKLCH
export function hslToOklch(h: number, s: number, l: number): OklchColor {
  const [r, g, b] = hslToRgb(h, s, l)
  const xyz = rgbToXyz(r ?? 0, g ?? 0, b ?? 0)
  const oklab = xyzToOklab(xyz.x, xyz.y, xyz.z)
  return oklabToOklch(oklab)
}

export function hexToHSL(hex: string) {
  // Remove the # if present
  const hexValue = hex.replace("#", "")

  // Convert hex to RGB
  const r = Number.parseInt(hexValue.substring(0, 2), 16) / 255
  const g = Number.parseInt(hexValue.substring(2, 4), 16) / 255
  const b = Number.parseInt(hexValue.substring(4, 6), 16) / 255

  // Find min and max values
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)

  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }

    h /= 6
  }

  // Convert to degrees and percentages
  h = Math.round(h * 360)
  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return { h, s, l }
}
